// Generated by GitHub Copilot - Aug-18-2026
import React, { useState, useEffect } from 'react';
import styles from './DisclaimerPopup.module.css';
import { CONFIDENTIALITY_EMAIL } from '../../constants/contacts';

const DISCLAIMER_STORAGE_KEY = 'gcc_disclaimer_accepted';

/**
 * Returns the localStorage key scoped to the signed-in account.
 * Falls back to a generic key so the popup still works when account is unavailable.
 * @param {import('@azure/msal-browser').AccountInfo | null} account
 * @returns {string}
 */
function storageKey(account) {
  return account?.homeAccountId
    ? `${DISCLAIMER_STORAGE_KEY}_${account.homeAccountId}`
    : DISCLAIMER_STORAGE_KEY;
}

/**
 * Returns true if the current account has already accepted the disclaimer.
 * @param {import('@azure/msal-browser').AccountInfo | null} account
 * @returns {boolean}
 */
export function hasAcceptedDisclaimer(account) {
  try {
    return localStorage.getItem(storageKey(account)) === 'true';
  } catch {
    return false;
  }
}

/**
 * DisclaimerPopup — shown once per user (persisted in localStorage) immediately
 * after a successful SSO sign-in. Requires the user to check "I Understand"
 * before enabling the Confirm button.
 *
 * Accessibility: focus is trapped inside the dialog; the overlay is not
 * dismissible by clicking outside so users must actively accept.
 *
 * @param {{ account: import('@azure/msal-browser').AccountInfo | null, onAccept: () => void }} props
 */
export default function DisclaimerPopup({ account, onAccept }) {
  const [checked, setChecked] = useState(false);

  // Lock body scroll while the modal is open; restore on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  /** Persists acceptance and notifies parent. */
  function handleConfirm() {
    if (!checked) return;
    try {
      localStorage.setItem(storageKey(account), 'true');
    } catch {
      // Storage unavailable — still let the user proceed for this session.
    }
    onAccept();
  }

  return (
    /* Overlay — not dismissible by click; user must confirm */
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="disclaimer-title">
      <div className={styles.modal}>
        <h2 className={styles.title} id="disclaimer-title">
          Confidentiality Disclaimer
        </h2>

        <p className={styles.body}>
          You are using an Accenture proprietary tool ('GCC Studio'). You understand that this
          tool may contain{' '}
          <strong>ACCENTURE, CLIENT, OR VENDOR / PARTNER CONFIDENTIAL INFORMATION.</strong> You are
          obligated to adhere to applicable confidentiality obligations and Accenture's policies,
          including Policy 69 - Confidentiality, when using the tool and information in the tool.
          Information in the tool can only be shared with those authorized to receive it. If you are
          downloading/exporting Confidential Information to another file or report, you must label
          that file or report as Accenture Confidential. If you have any questions, please email
          your question to{' '}
          <a
            className={styles.emailLink}
            href={`mailto:${CONFIDENTIALITY_EMAIL}`}
            /* Security: mailto only; no dynamic URL construction */
          >
            {CONFIDENTIALITY_EMAIL}
          </a>{' '}
          or contact your Leadership. Thank you!
        </p>

        <div className={styles.checkboxRow}>
          <input
            type="checkbox"
            id="disclaimer-check"
            className={styles.checkbox}
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            aria-required="true"
          />
          <label htmlFor="disclaimer-check" className={styles.checkboxLabel}>
            I Understand
          </label>
        </div>

        <div className={styles.footer}>
          <button
            className={`${styles.confirmBtn} ${!checked ? styles.confirmBtnDisabled : ''}`}
            onClick={handleConfirm}
            disabled={!checked}
            aria-disabled={!checked}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
